/**
 * Application Constants - EduQ AI Admin Portal
 * 
 * Centralized configuration for timeouts, limits, and other constants
 */

// ============================================================================
// SESSION & AUTHENTICATION
// ============================================================================

/** Session timeout in milliseconds (30 minutes) */
export const SESSION_TIMEOUT = 30 * 60 * 1000;

/** Session warning time before timeout (5 minutes) */
export const SESSION_WARNING_TIME = 5 * 60 * 1000;

/** Token refresh interval in milliseconds (25 minutes) */
export const TOKEN_REFRESH_INTERVAL = 25 * 60 * 1000;

// ============================================================================
// DATA REFRESH INTERVALS
// ============================================================================

/** Dashboard metrics refresh interval (30 seconds) */
export const DASHBOARD_REFRESH_INTERVAL = 30 * 1000;

/** Real-time monitoring refresh interval (30 seconds) */
export const MONITORING_REFRESH_INTERVAL = 30 * 1000;

/** System health check interval (60 seconds) */
export const HEALTH_CHECK_INTERVAL = 60 * 1000;

/** Audit log refresh interval (60 seconds) */
export const AUDIT_LOG_REFRESH_INTERVAL = 60 * 1000;

// ============================================================================
// PAGINATION & LIMITS
// ============================================================================

/** Default page size for tables */
export const DEFAULT_PAGE_SIZE = 25;

/** Page size options for pagination */
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

/** Maximum items per page */
export const MAX_PAGE_SIZE = 100;

/** Maximum search results to display */
export const MAX_SEARCH_RESULTS = 100;

// ============================================================================
// DATA EXPORT
// ============================================================================

/** Threshold for async export processing (records) */
export const EXPORT_ASYNC_THRESHOLD = 10000;

/** Maximum records for immediate export */
export const EXPORT_MAX_IMMEDIATE = 10000;

/** Export formats */
export const EXPORT_FORMATS = {
  CSV: 'csv',
  JSON: 'json',
};

/** Export data types */
export const EXPORT_DATA_TYPES = {
  USERS: 'users',
  ANALYTICS: 'analytics',
  TRANSACTIONS: 'transactions',
  AUDIT_LOGS: 'audit_logs',
};

// ============================================================================
// MONITORING & ALERTS
// ============================================================================

/** Default error rate threshold (percentage) */
export const DEFAULT_ERROR_THRESHOLD = 5;

/** Default latency threshold (milliseconds) */
export const DEFAULT_LATENCY_THRESHOLD = 3000;

/** Alert check interval (30 seconds) */
export const ALERT_CHECK_INTERVAL = 30 * 1000;

// ============================================================================
// CACHE SETTINGS
// ============================================================================

/** Cache TTL for dashboard data (5 minutes) */
export const CACHE_TTL_DASHBOARD = 5 * 60 * 1000;

/** Cache TTL for user data (10 minutes) */
export const CACHE_TTL_USER_DATA = 10 * 60 * 1000;

/** Cache TTL for analytics data (15 minutes) */
export const CACHE_TTL_ANALYTICS = 15 * 60 * 1000;

/** Cache TTL for system metrics (1 minute) */
export const CACHE_TTL_METRICS = 60 * 1000;

// ============================================================================
// UI SETTINGS
// ============================================================================

/** Toast notification duration (milliseconds) */
export const TOAST_DURATION = 5000;

/** Debounce delay for search input (milliseconds) */
export const SEARCH_DEBOUNCE_DELAY = 300;

/** Animation duration (milliseconds) */
export const ANIMATION_DURATION = 200;

/** Modal transition duration (milliseconds) */
export const MODAL_TRANSITION_DURATION = 300;

// ============================================================================
// VALIDATION LIMITS
// ============================================================================

/** Maximum ticket title length */
export const MAX_TICKET_TITLE_LENGTH = 200;

/** Maximum ticket description length */
export const MAX_TICKET_DESCRIPTION_LENGTH = 5000;

/** Maximum notification content length */
export const MAX_NOTIFICATION_CONTENT_LENGTH = 1000;

/** Maximum feature flag name length */
export const MAX_FEATURE_FLAG_NAME_LENGTH = 100;

// ============================================================================
// STATUS VALUES
// ============================================================================

/** Ticket status values */
export const TICKET_STATUS = {
  OPEN: 'open',
  IN_PROGRESS: 'in-progress',
  RESOLVED: 'resolved',
  CLOSED: 'closed',
};

/** Ticket priority values */
export const TICKET_PRIORITY = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  CRITICAL: 'critical',
};

/** User account status values */
export const USER_STATUS = {
  ACTIVE: 'active',
  DISABLED: 'disabled',
  SUSPENDED: 'suspended',
  PENDING: 'pending',
};

/** Subscription status values */
export const SUBSCRIPTION_STATUS = {
  ACTIVE: 'active',
  CANCELLED: 'cancelled',
  EXPIRED: 'expired',
  TRIAL: 'trial',
};

/** Payment status values */
export const PAYMENT_STATUS = {
  PENDING: 'pending',
  COMPLETED: 'completed',
  FAILED: 'failed',
  REFUNDED: 'refunded',
};

// ============================================================================
// FEATURE FLAGS
// ============================================================================

/** Feature flag target types */
export const FEATURE_FLAG_TARGET_TYPES = {
  ALL: 'all',
  GROUP: 'group',
  USER: 'user',
  PERCENTAGE: 'percentage',
  REGION: 'region',
};

// ============================================================================
// COGNITO GROUPS
// ============================================================================

/** Admin user groups */
export const ADMIN_GROUPS = {
  SUPER_ADMIN: 'SuperAdmins',
  ADMIN: 'Admins',
};

// ============================================================================
// AWS CONFIGURATION
// ============================================================================

/** AWS Region */
export const AWS_REGION = 'ap-southeast-1';

/** Cognito User Pool ID */
export const USER_POOL_ID = 'ap-southeast-1_ISUlRZfpp';

// ============================================================================
// DATE FORMATS
// ============================================================================

/** Date format for display */
export const DATE_FORMAT = 'MMM DD, YYYY';

/** Date-time format for display */
export const DATETIME_FORMAT = 'MMM DD, YYYY HH:mm:ss';

/** Date format for API requests */
export const API_DATE_FORMAT = 'YYYY-MM-DD';

/** Date-time format for API requests */
export const API_DATETIME_FORMAT = 'YYYY-MM-DDTHH:mm:ss.SSSZ';

// ============================================================================
// ERROR MESSAGES
// ============================================================================

export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Network error. Please check your connection and try again.',
  UNAUTHORIZED: 'You are not authorized to perform this action.',
  SESSION_EXPIRED: 'Your session has expired. Please log in again.',
  SERVER_ERROR: 'Server error. Please try again later.',
  VALIDATION_ERROR: 'Please check your input and try again.',
  NOT_FOUND: 'The requested resource was not found.',
};

// ============================================================================
// SUCCESS MESSAGES
// ============================================================================

export const SUCCESS_MESSAGES = {
  SAVE_SUCCESS: 'Changes saved successfully.',
  DELETE_SUCCESS: 'Item deleted successfully.',
  CREATE_SUCCESS: 'Item created successfully.',
  UPDATE_SUCCESS: 'Item updated successfully.',
};

export default {
  SESSION_TIMEOUT,
  SESSION_WARNING_TIME,
  TOKEN_REFRESH_INTERVAL,
  DASHBOARD_REFRESH_INTERVAL,
  MONITORING_REFRESH_INTERVAL,
  HEALTH_CHECK_INTERVAL,
  AUDIT_LOG_REFRESH_INTERVAL,
  DEFAULT_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
  MAX_PAGE_SIZE,
  MAX_SEARCH_RESULTS,
  EXPORT_ASYNC_THRESHOLD,
  EXPORT_MAX_IMMEDIATE,
  EXPORT_FORMATS,
  EXPORT_DATA_TYPES,
  DEFAULT_ERROR_THRESHOLD,
  DEFAULT_LATENCY_THRESHOLD,
  ALERT_CHECK_INTERVAL,
  CACHE_TTL_DASHBOARD,
  CACHE_TTL_USER_DATA,
  CACHE_TTL_ANALYTICS,
  CACHE_TTL_METRICS,
  TOAST_DURATION,
  SEARCH_DEBOUNCE_DELAY,
  ANIMATION_DURATION,
  MODAL_TRANSITION_DURATION,
  MAX_TICKET_TITLE_LENGTH,
  MAX_TICKET_DESCRIPTION_LENGTH,
  MAX_NOTIFICATION_CONTENT_LENGTH,
  MAX_FEATURE_FLAG_NAME_LENGTH,
  TICKET_STATUS,
  TICKET_PRIORITY,
  USER_STATUS,
  SUBSCRIPTION_STATUS,
  PAYMENT_STATUS,
  FEATURE_FLAG_TARGET_TYPES,
  ADMIN_GROUPS,
  AWS_REGION,
  USER_POOL_ID,
  DATE_FORMAT,
  DATETIME_FORMAT,
  API_DATE_FORMAT,
  API_DATETIME_FORMAT,
  ERROR_MESSAGES,
  SUCCESS_MESSAGES,
};
